import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, vShow as _vShow, withDirectives as _withDirectives, isRef as _isRef, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["data-dusk"]
const _hoisted_2 = { class: "border-t border-gray-200 mt-6" }
const _hoisted_3 = { class: "grid grid-cols-5 gap-6 mt-6" }
const _hoisted_4 = { class: "mt-8" }
const _hoisted_5 = {
  "data-dusk": "power-selection",
  class: "mt-4"
}
const _hoisted_6 = { class: "mt-4 flex justify-end" }

import { computed, ref } from 'vue';
import { BaseButton } from '@/lib/components/Button';
import { SelectInput } from '@/lib/components/Select';
import { BaseIcon } from '@/lib/components/Icon';
import { PowerSelection } from '@/lib/components/PowerSelection';
import { useI18n } from 'vue-i18n';
import { useProductCard } from '@/composables/use-product-card';
import type { LensProperty, ProductImage, Variant, HighlightBgColor } from '@/models/webshop.model';
import { useRoute } from 'vue-router';
import { translateBoolean } from '@/lib/helpers/string.helper';
import ProductPrimaryInfo from './ProductPrimaryInfo.vue';
import BaseAlert from '../Banner/BaseAlert.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductCardMultiPower',
  props: {
    mlsId: {},
    productCategory: {},
    productCategorySlug: {},
    productCategoryDisplayName: {},
    title: {},
    image: {},
    lensProperties: {},
    variants: {},
    lensCode: {},
    dataDusk: {},
    highlightText: {},
    highlightColor: {}
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n();
const route = useRoute();
const currentOrganisationId = computed(() => route.params.organisationId as string);
const powerSelectionRef = ref<InstanceType<typeof PowerSelection> | null>(null);
const hasPowerFieldBlurred = ref(false);

const {
  dynamicParameters,
  displayableProperties,
  dynamicPropertyOptions,
  pack,
  showPowerSelection,
  selectedPowerQuantities,
  packOptions,
  powerOptions,
  isAddToCartDisabled,
  handleAddToCart,
  isLoadingParameters,
  calculateLensProperties,
  canBeOrdered,
  invalidPropertyErrors
} = useProductCard({
  mlsId: props.mlsId,
  productName: props.title,
  productCategory: props.productCategory,
  productCategorySlug: props.productCategorySlug,
  productCategoryDisplayName: props.productCategoryDisplayName,
  lensProperties: props.lensProperties,
  variants: props.variants,
  image: props.image,
  lensCode: props.lensCode,
  powerSelectionMode: 'multi',
  powerSelectionRef,
  organisationId: currentOrganisationId
});

const translatedDynamicPropertyOptions = computed(() => ({
  ...dynamicPropertyOptions.value,
  TrialLens: dynamicPropertyOptions.value?.TrialLens?.map((option) => ({
    ...option,
    label: translateBoolean(option.label, t)
  }))
}));

// Helper to get translation key for property names
const getPropertyLabel = (propertyName: string): string => {
  const key = `platform.webshop.${propertyName.toLowerCase()}`;
  const translated = t(key);
  // If translation is missing, i18n returns the key itself
  if (translated === key) {
    return propertyName
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      .trim();
  }
  return translated;
};

const getPropertyPlaceholder = (propertyName: string): string => {
  const key = `platform.webshop.placeHolders.${propertyName.toLowerCase()}`;
  const translated = t(key);
  return translated === key ? t('platform.webshop.placeHolders.radius') : translated;
};

// Filter out Power property for multi-power mode (it's handled separately)
const nonPowerProperties = computed(() => {
  const list = Array.isArray(displayableProperties?.value) ? displayableProperties.value : [];
  return list.filter((p) => p?.name !== 'Power');
});

const curSelection = computed(() => {
  const values = Object.entries(dynamicParameters)
    .filter(([key]) => key !== 'Power')
    .map(([, value]) => value);
  const firstFalsyIndex = values.findIndex((value) => !value);
  return firstFalsyIndex === -1 ? values.length : firstFalsyIndex;
});

const allParametersFilled = computed(() => {
  const nonPowerProps = displayableProperties.value.filter((p) => p.name !== 'Power');
  const allPropsFilledOrEmpty = nonPowerProps.every((property) => {
    const options = dynamicPropertyOptions.value[property.name] ?? [];
    return options.length === 0 || dynamicParameters[property.name] !== null;
  });

  const packOptionsList = Array.isArray(packOptions.value) ? packOptions.value : [];
  const packFilledOrEmpty = packOptionsList.length === 0 || pack.value !== null;

  const hasPowerQuantities = selectedPowerQuantities.value.some((pq) => pq.quantity > 0);

  return allPropsFilledOrEmpty && packFilledOrEmpty && hasPowerQuantities && hasPowerFieldBlurred.value;
});

const hasInvalidSelectedProperties = computed(() =>
  Object.keys(invalidPropertyErrors.value).length > 0
);

const showCombinationNotAvailableWarning = computed(() =>
  !isLoadingParameters.value && (
    hasInvalidSelectedProperties.value ||
    (!canBeOrdered.value && allParametersFilled.value)
  )
);

const cardDusk = computed(() => props.dataDusk || 'product-card-id');

const onPowerFieldChanged = (opt: {power: string, quantity: number | null}) => {
  if (!opt.quantity) {
    return;
  }
  hasPowerFieldBlurred.value = true;
  calculateLensProperties({ propertyName: 'Power', value: opt.power });
};
const handlePropertyChange = (
  propertyName: string,
  value: string | null
) => {
  if (value === null || value === '') {
    return;
  }

  calculateLensProperties({ propertyName, value });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "w-full",
    "data-dusk": cardDusk.value
  }, [
    _createVNode(ProductPrimaryInfo, {
      title: props.title,
      image: props.image,
      "highlight-text": props.highlightText,
      "highlight-color": props.highlightColor
    }, null, 8, ["title", "image", "highlight-text", "highlight-color"]),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nonPowerProperties.value, (property, index) => {
          return _withDirectives((_openBlock(), _createElementBlock("div", {
            key: property.name
          }, [
            _createVNode(_unref(SelectInput), {
              modelValue: _unref(dynamicParameters)[property.name],
              "onUpdate:modelValue": [($event: any) => ((_unref(dynamicParameters)[property.name]) = $event), ($event: any) => (handlePropertyChange(property.name, $event))],
              disabled: !(index <= curSelection.value),
              label: getPropertyLabel(property.name),
              placeholder: getPropertyPlaceholder(property.name),
              options: (translatedDynamicPropertyOptions.value && translatedDynamicPropertyOptions.value[property.name]) || [],
              clear: true,
              error: _unref(invalidPropertyErrors)[property.name],
              "data-dusk": `${property.name.toLowerCase()}-select`,
              loading: _unref(isLoadingParameters)
            }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled", "label", "placeholder", "options", "error", "data-dusk", "loading"])
          ])), [
            [_vShow, translatedDynamicPropertyOptions.value && translatedDynamicPropertyOptions.value[property.name]?.length > 0]
          ])
        }), 128)),
        _createElementVNode("div", null, [
          _createVNode(_unref(SelectInput), {
            modelValue: _unref(pack),
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(pack) ? (pack).value = $event : null)),
            disabled: curSelection.value < nonPowerProperties.value.length || _unref(packOptions).length === 0,
            label: _unref(t)('platform.webshop.pack'),
            placeholder: _unref(t)('platform.webshop.placeHolders.pack'),
            loading: _unref(isLoadingParameters),
            options: _unref(packOptions),
            clear: true,
            "data-dusk": "pack-select"
          }, null, 8, ["modelValue", "disabled", "label", "placeholder", "loading", "options"])
        ])
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("p", {
          class: "text-sm font-medium cursor-pointer select-none text-center flex items-center justify-center gap-3",
          "data-dusk": "show-power-selection",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (showPowerSelection.value = !_unref(showPowerSelection)))
        }, [
          _createElementVNode("span", null, _toDisplayString(_unref(showPowerSelection) ? _unref(t)('platform.webshop.hide-power') : _unref(t)('platform.webshop.show-power')), 1),
          _createVNode(_unref(BaseIcon), {
            name: _unref(showPowerSelection) ? 'product-arrow-up' : 'product-arrow-down',
            class: _normalizeClass(["text-sm stroke-1.5", 'text-gray-500'])
          }, null, 8, ["name"])
        ]),
        _withDirectives(_createElementVNode("div", _hoisted_5, [
          _createVNode(_unref(PowerSelection), {
            ref_key: "powerSelectionRef",
            ref: powerSelectionRef,
            modelValue: _unref(showPowerSelection),
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(showPowerSelection) ? (showPowerSelection).value = $event : null)),
            "power-options": _unref(powerOptions),
            "onUpdate:powerOnBlur": onPowerFieldChanged,
            "onUpdate:powerQuantities": _cache[3] || (_cache[3] = ($event: any) => (selectedPowerQuantities.value = $event))
          }, null, 8, ["modelValue", "power-options"])
        ], 512), [
          [_vShow, _unref(showPowerSelection)]
        ])
      ]),
      (showCombinationNotAvailableWarning.value)
        ? (_openBlock(), _createBlock(BaseAlert, {
            key: 0,
            class: "mt-6",
            color: "danger",
            icon: "halt",
            label: _unref(t)('platform.webshop.combination-not-available')
          }, null, 8, ["label"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_6, [
        _createVNode(_unref(BaseButton), {
          size: "large",
          "left-icon": 'add',
          class: "px-6 py-3 bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2",
          disabled: _unref(isAddToCartDisabled),
          "data-dusk": "add-to-cart-button",
          onClick: _unref(handleAddToCart)
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('platform.webshop.addToCart')), 1)
          ]),
          _: 1
        }, 8, ["disabled", "onClick"])
      ])
    ])
  ], 8, _hoisted_1))
}
}

})